import { useReducer, useState, useCallback } from "react";

import { toast } from "react-toastify";

import { gameReducer } from "./Reducer";
import { startingGame } from "../Context/Context";
import {
  Location,
  Ray,
  Tally,
  Penalties,
  RayPath,
  GameState,
  FetchResultSetter,
  RayPaths,
  Step,
  RayResult,
} from "../../../types/GameTypes";
import play from "../Audio";
import { FetchResult } from "../../../services/doFetch";
import { shootRay, fetchGame, deleteGuessAt, putGuessAt, submitGuesses } from "../../../services/GameService";
import useAsyncError from "../../../services/useAsyncError";

export type Engine = {
  arePathsRevealed: () => boolean;
  getBallCount: () => number;
  getGuessCount: () => number;
  getNonce: () => string;
  getPathFor: (ordinal: number) => RayPath | undefined;
  getPenaltyFor: (result: RayResult) => number;
  getRayFor: (ordinal: number) => Ray | undefined;
  getRevealedStepsFor: (location: Location) => Array<Step>;
  getScore: () => number;
  getState: () => string;
  getTally: () => Tally | undefined;
  hasAllGuesses: () => boolean;
  hasBallAt: (location: Location) => boolean;
  hasGuessAt: (location: Location) => boolean;
  isComplete: () => boolean;
  isRunning: () => boolean;
  isVoid: () => boolean;
  loadGame: (nonce: string, setFetchResult?: FetchResultSetter<GameState>) => void;
  removePaths: () => void;
  shootRayInto: (ordinal: number, setFetchResult: FetchResultSetter<Ray>) => void;
  showAllPaths: () => void;
  showPathFor: (ordinal: number) => void;
  submitGuesses: () => void;
  toggleGuessAt: (location: Location) => void;
};

export function useEngine(): Engine {
  const [game, dispatch] = useReducer(gameReducer, startingGame);
  const [paths, setPaths] = useState<RayPaths>([]);

  const throwError = useAsyncError();

  const hasGuessAt = useCallback(
    (location: Location): boolean => {
      return game.state === "VOID"
        ? false
        : !!game.guesses.find((guess) => guess.location.x === location.x && guess.location.y === location.y);
    },
    [game]
  );

  const getPathFor = useCallback(
    (ordinal: number): RayPath | undefined => {
      if (game.state !== "COMPLETE") return undefined;
      const rayPath = game.rayPaths.find(
        (path) => path.ray.entryPortOrdinal === ordinal || path.ray.exitPortOrdinal === ordinal
      );
      return rayPath || undefined;
    },
    [game]
  );

  return {
    getNonce: useCallback(() => (game.state !== "VOID" ? game.nonce : ""), [game]),

    getState: useCallback(() => game.state, [game]),

    hasGuessAt,

    hasBallAt: useCallback(
      (location: Location): boolean => {
        return (
          game.state === "COMPLETE" &&
          !!game.balls &&
          !!game.balls.find((ball) => ball.location.x === location.x && ball.location.y === location.y)
        );
      },
      [game]
    ),

    getRayFor: useCallback(
      (ordinal: number): Ray | undefined => {
        return game.state === "VOID"
          ? undefined
          : game.rays.find(
              (ray: Ray): boolean => ray.entryPortOrdinal === ordinal || ray.exitPortOrdinal === ordinal
            );
      },
      [game]
    ),

    isRunning: useCallback((): boolean => {
      return game.state === "RUNNING";
    }, [game]),

    isComplete: useCallback((): boolean => {
      return game.state === "COMPLETE";
    }, [game]),

    isVoid: useCallback((): boolean => {
      return game.state === "VOID";
    }, [game]),

    hasAllGuesses: useCallback((): boolean => {
      return game.state === "VOID" ? false : game.guesses.length === game.ballCount;
    }, [game]),

    getGuessCount: useCallback((): number => {
      return game.state === "VOID" ? 0 : game.guesses.length;
    }, [game]),

    getBallCount: useCallback((): number => {
      return game.state === "VOID" ? 0 : game.ballCount;
    }, [game]),

    getTally: useCallback((): Tally | undefined => {
      //TODO: fixup refs which don't expect undefined
      return game.state === "COMPLETE" ? game.tally : undefined;
    }, [game]),

    getScore: useCallback((): number => {
      return game.state === "VOID" ? 0 : game.score;
    }, [game]),

    getPenaltyFor: useCallback((result: RayResult): number => {
      //TODO: fixup refs to scoreFor
      switch (result) {
        case "DETOUR":
          return Penalty.detour;
        case "HIT":
          return Penalty.hit;
        case "REFLECTION":
          return Penalty.reflection;
        default:
          throw new Error(`Unknown result type ${result}`);
      }
    }, []),

    getPathFor: useCallback(
      (ordinal: number): RayPath | undefined => {
        if (game.state !== "COMPLETE") return undefined;
        const rayPath = game.rayPaths.find(
          (path) => path.ray.entryPortOrdinal === ordinal || path.ray.exitPortOrdinal === ordinal
        );
        return rayPath || undefined;
      },
      [game]
    ),

    // async state-changers:

    loadGame: useCallback(
      async (nonce: string, setFetchResult?: FetchResultSetter<GameState>) => {
        const result: FetchResult<GameState> = await fetchGame(nonce, setFetchResult);
        if (result.error) {
          throwError(result.error);
        } else {
          dispatch({
            type: "LOAD",
            payload: result.status && result.status.status !== 200 ? { state: "VOID" } : result.data!,
          });
        }
      },
      [throwError]
    ),

    shootRayInto: useCallback(
      async (ordinal: number, setFetchResult: FetchResultSetter<Ray>) => {
        if (game.state !== "RUNNING") return;
        play("RAY");
        const result: FetchResult<Ray> = await shootRay(game.nonce, ordinal, setFetchResult);
        if (result.error) {
          alertError("Shooting ray failed.");
        } else {
          const ray: Ray | undefined = result.data;
          if (ray) {
            dispatch({ type: "ADD_RAY", payload: ray });
            playResultSound(ray.result);
          }
        }
      },
      [game]
    ),

    toggleGuessAt: useCallback(
      async (location: Location) => {
        if (game.state === "VOID") return;
        const { x, y }: Location = location;
        if (hasGuessAt(location)) {
          dispatch({ type: "REMOVE_GUESS", payload: { x, y } });
          play("REMOVED");
          const result = await deleteGuessAt(game.nonce, x, y);
          if (result.error) {
            dispatch({ type: "ADD_GUESS", payload: { x, y } });
            alertError("Guess removal failed.");
          }
        } else {
          if (game.guesses.length < game.ballCount) {
            dispatch({ type: "ADD_GUESS", payload: { x, y } });
            play("PLACED");
            const result = await putGuessAt(game.nonce, x, y);
            if (result.error) {
              dispatch({ type: "REMOVE_GUESS", payload: { x, y } });
              alertError("Guess placement failed.");
            }
          } else {
            toast.warning(
              "You have placed the maximum amount of guesses. Please remove a guess before choosing another."
            );
            play("BONK");
          }
        }
      },
      [game, hasGuessAt]
    ),

    submitGuesses: useCallback(async () => {
      if (game.state === "VOID") return;
      if (game.guesses.length === game.ballCount) {
        const result: FetchResult<GameState> = await submitGuesses(game.nonce);
        if (result.error) {
          alertError("Submitting your solution failed.");
        } else {
          dispatch({ type: "LOAD", payload: result.data! });
          if (result.data?.state === "COMPLETE") {
            const completed = result.data;
            play(completed.tally.missCount === 0 ? "DRUMSET" : "DUDUN");
          }
        }
      }
    }, [game]),

    showAllPaths: useCallback((): void => {
      if (game.state === "COMPLETE") {
        setPaths(game.rayPaths);
        play("ACTIVATE");
      }
    }, [game, setPaths]),

    removePaths: useCallback((): void => {
      if (game.state === "COMPLETE") {
        setPaths([]);
        play("DEACTIVATED");
      }
    }, [setPaths, game]),

    showPathFor: useCallback(
      (ordinal: number): void => {
        if (game.state === "COMPLETE") {
          const path = getPathFor(ordinal);
          if (path) {
            setPaths([path]);
            playResultSound(path.ray.result);
          }
        }
      },
      [setPaths, game, getPathFor]
    ),

    getRevealedStepsFor: useCallback(
      (target: Location): Array<Step> => {
        let steps: Array<Step> = [];
        if (game.state === "COMPLETE" && paths.length) {
          paths.forEach((path) => {
            const stepsInPath = path.steps.filter(
              ({ location }) => location.x === target.x && location.y === target.y
            );
            steps = [...steps, ...stepsInPath];
          });
        }
        return steps;
      },
      [paths, game.state]
    ),

    arePathsRevealed: useCallback(() => {
      return !!paths.length;
    }, [paths]),
  };
}

export const Penalty: Penalties = {
  hit: 1,
  reflection: 1,
  detour: 2,
  missed: 5,
};

export function playResultSound(result: RayResult) {
  play(result);
}

function alertError(message: string) {
  toast.error(
    `${message} This is likely a server error. Please wait a few minutes 
    and try the action again. You can always bookmark the game URL 
    and reload it later to pick up your game where you left off.`,
    { autoClose: false }
  );
  play("DRAMA");
}
